import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { MemoryItem, MemoryLocation } from "../types";

// =============================================================================
// Types
// =============================================================================

interface MemoryPanelProps {
  items: MemoryItem[];
  onPin: (memoryId: string) => void;
  onBurn: (memoryId: string) => void;
  onBurnAll: () => void;
  allowPinning: boolean;
}

// =============================================================================
// Design System Colors
// =============================================================================

const HVME_PRIMARY = "#DCA0FA"; // Plasma Violet
const HVME_ACCENT = "#E6BEFA"; // Aurora Lilac

// =============================================================================
// Helpers
// =============================================================================

function formatTTL(seconds: number): string {
  if (seconds <= 0) return "Expired";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function ttlPercentage(item: MemoryItem): number {
  if (item.pinned) return 100;
  if (item.ttlSeconds <= 0) return 0;
  return Math.max(0, Math.min(100, (item.remainingSeconds / item.ttlSeconds) * 100));
}

function ttlColor(percentage: number): string {
  if (percentage > 60) return "bg-hvme-green/60";
  if (percentage > 25) return "bg-hvme-gold/60";
  return "bg-hvme-red/60";
}

const LOCATION_LABELS: Record<MemoryLocation, { label: string; color: string }> = {
  device: { label: "Device", color: "text-hvme-primary" },
  redis: { label: "Redis", color: "text-hvme-accent" },
  pinned: { label: "Pinned", color: "text-hvme-green" },
};

// =============================================================================
// Confirmation Dialog
// =============================================================================

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  variant,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  variant: "danger" | "warning";
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-hvme-bg-deep/80 backdrop-blur-sm"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="glass-panel p-6 max-w-sm w-full mx-4 border border-hvme-accent/20 shadow-[0_0_20px_rgba(230,190,250,0.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-heading text-base font-semibold text-hvme-text-primary mb-2">
          {title}
        </h3>
        <p className="font-body text-sm text-hvme-text-secondary mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="font-body px-4 py-2 text-sm text-hvme-text-secondary hover:text-hvme-text-primary rounded-lg border border-hvme-border hover:border-hvme-primary/50 hover:shadow-[0_0_12px_rgba(220,160,250,0.2)] transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`
              font-body px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200
              ${variant === "danger"
                ? "bg-hvme-red/20 text-hvme-red border border-hvme-red/30 hover:bg-hvme-red/30 hover:shadow-[0_0_16px_rgba(255,107,122,0.25)]"
                : "bg-hvme-accent/20 text-hvme-accent border border-hvme-accent/30 hover:bg-hvme-accent/30 hover:shadow-[0_0_16px_rgba(230,190,250,0.25)]"
              }
            `}
          >
            {confirmLabel}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// =============================================================================
// Memory Item Row
// =============================================================================

function MemoryRow({
  item,
  onPin,
  onBurn,
  allowPinning,
}: {
  item: MemoryItem;
  onPin: (id: string) => void;
  onBurn: (id: string) => void;
  allowPinning: boolean;
}) {
  const [confirmPin, setConfirmPin] = useState(false);
  const pct = ttlPercentage(item);
  const isExpiring = pct < 15 && !item.pinned;
  const locationInfo = LOCATION_LABELS[item.location];

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{
          opacity: item.remainingSeconds <= 0 && !item.pinned ? 0.3 : 1,
          y: 0,
        }}
        exit={{ opacity: 0, height: 0, marginBottom: 0, y: -8 }}
        transition={{ duration: 0.3 }}
        className={`
          glass-panel p-4 transition-all duration-300
          hover:border-hvme-primary/30 hover:shadow-[0_0_16px_rgba(220,160,250,0.1)]
          ${isExpiring ? "border-hvme-red/30" : ""}
        `}
      >
        <div className="flex items-start gap-4">
          {/* TTL indicator */}
          <div className="flex flex-col items-center gap-1 min-w-[52px]">
            <div className="w-10 h-10 rounded-full border border-hvme-border bg-hvme-bg-deep flex items-center justify-center relative">
              {/* Circular progress */}
              <svg className="absolute inset-0 w-10 h-10 -rotate-90" viewBox="0 0 40 40">
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke="rgba(42, 42, 58, 0.5)"
                  strokeWidth="2"
                />
                <motion.circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke={
                    item.pinned
                      ? HVME_PRIMARY
                      : pct > 60
                        ? "#4aff7a"
                        : pct > 25
                          ? HVME_ACCENT
                          : "#ff4a5a"
                  }
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={`${pct} ${100 - pct}`}
                  pathLength="100"
                  animate={{ strokeDasharray: `${pct} ${100 - pct}` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </svg>
              {item.pinned ? (
                <span className="text-[10px] text-hvme-primary font-mono z-10">PIN</span>
              ) : (
                <span className="text-[10px] text-hvme-text-muted font-mono z-10">
                  {formatTTL(item.remainingSeconds)}
                </span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-heading text-sm font-medium text-hvme-text-primary truncate">
                {item.key}
              </span>
              <span
                className={`font-body text-[10px] font-mono uppercase ${locationInfo.color}`}
              >
                {locationInfo.label}
              </span>
            </div>
            <p className="font-body text-xs text-hvme-text-muted truncate mb-2">
              {item.preview}
            </p>
            <div className="flex items-center gap-3">
              {/* TTL bar */}
              <div className="flex-1 h-1 rounded-full bg-hvme-bg-deep overflow-hidden max-w-[200px]">
                <motion.div
                  className={`h-full rounded-full ${ttlColor(pct)}`}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
              <span className="font-body text-[10px] text-hvme-text-muted font-mono">
                {formatBytes(item.sizeBytes)}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {allowPinning && !item.pinned && (
              <button
                onClick={() => setConfirmPin(true)}
                className="font-body px-3 py-1.5 text-xs rounded-lg border border-hvme-border text-hvme-text-secondary hover:text-hvme-primary hover:border-hvme-primary/40 hover:shadow-[0_0_12px_rgba(220,160,250,0.2)] transition-all duration-200"
              >
                Pin
              </button>
            )}
            <button
              onClick={() => onBurn(item.id)}
              className="font-body px-3 py-1.5 text-xs rounded-lg border border-hvme-red/20 text-hvme-red/70 hover:text-hvme-red hover:border-hvme-red/40 hover:bg-hvme-red/5 hover:shadow-[0_0_12px_rgba(255,74,90,0.15)] transition-all duration-200"
            >
              Burn
            </button>
          </div>
        </div>
      </motion.div>

      {/* Pin confirmation */}
      <AnimatePresence>
        {confirmPin && (
          <ConfirmDialog
            open={confirmPin}
            title="Pin Memory Item"
            message={`Pinning "${item.key}" will persist it beyond its TTL. This may conflict with your privacy policy. Continue?`}
            confirmLabel="Pin Item"
            variant="warning"
            onConfirm={() => {
              onPin(item.id);
              setConfirmPin(false);
            }}
            onCancel={() => setConfirmPin(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// =============================================================================
// Memory Panel (Main Component)
// =============================================================================

export function MemoryPanel({
  items,
  onPin,
  onBurn,
  onBurnAll,
  allowPinning,
}: MemoryPanelProps) {
  const [confirmBurnAll, setConfirmBurnAll] = useState(false);
  const [shaking, setShaking] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Simulated TTL countdown (client-side)
  useEffect(() => {
    // In production, TTL updates come via WebSocket mem.ttl events.
    // This is a local fallback for demo/offline mode.
    intervalRef.current = setInterval(() => {
      // TTL countdown would be handled by parent state updates
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleBurnAllClick = useCallback(() => {
    setShaking(true);
    setTimeout(() => setShaking(false), 500);
    setConfirmBurnAll(true);
  }, []);

  const totalSize = items.reduce((sum, item) => sum + item.sizeBytes, 0);
  const pinnedCount = items.filter((i) => i.pinned).length;
  const expiringCount = items.filter(
    (i) => !i.pinned && ttlPercentage(i) < 25,
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold text-hvme-text-primary">
            Scratch Memory
          </h2>
          <p className="font-body text-xs text-hvme-text-muted mt-1">
            {items.length} items &middot; {formatBytes(totalSize)} total
            {pinnedCount > 0 && (
              <span className="text-hvme-primary">
                {" "}
                &middot; {pinnedCount} pinned
              </span>
            )}
            {expiringCount > 0 && (
              <span className="text-hvme-accent">
                {" "}
                &middot; {expiringCount} expiring soon
              </span>
            )}
          </p>
        </div>

        {/* Burn All button */}
        <motion.button
          onClick={handleBurnAllClick}
          animate={shaking ? { x: [-3, 3, -3, 3, 0] } : {}}
          transition={{ duration: 0.4 }}
          disabled={items.length === 0}
          className={`
            font-body px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200
            bg-hvme-red/10 text-hvme-red border-2 border-hvme-red/30
            hover:bg-hvme-red/20 hover:border-hvme-red/50 hover:shadow-[0_0_16px_rgba(255,107,122,0.25)]
            active:scale-95
            disabled:opacity-30 disabled:cursor-not-allowed
          `}
        >
          Burn All
        </motion.button>
      </div>

      {/* Memory items list */}
      {items.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass-panel p-12 text-center border border-hvme-primary/10"
        >
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-hvme-primary/30 bg-hvme-primary/5 flex items-center justify-center">
            <span className="text-lg text-hvme-primary/60">&empty;</span>
          </div>
          <p className="font-body text-sm text-hvme-text-muted">
            No scratch memory items. Memory entries will appear here as agents
            process tasks.
          </p>
        </motion.div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence mode="popLayout">
            {items.map((item) => (
              <MemoryRow
                key={item.id}
                item={item}
                onPin={onPin}
                onBurn={onBurn}
                allowPinning={allowPinning}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Burn All confirmation */}
      <AnimatePresence>
        {confirmBurnAll && (
          <ConfirmDialog
            open={confirmBurnAll}
            title="Burn All Memory"
            message={`This will permanently delete all ${items.length} memory items, including pinned items. This action cannot be undone.`}
            confirmLabel="Burn Everything"
            variant="danger"
            onConfirm={() => {
              onBurnAll();
              setConfirmBurnAll(false);
            }}
            onCancel={() => setConfirmBurnAll(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
